import { Avatar, Box, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import CourseInfoBox from "../../CourseDetailsPage/CourseInfoBox";
import Meta from "../../Meta/Meta";
import { fetchEnrolledCourseDetails } from "./../../../redux/Actions/enrolledCourseDetailsActions";
import LoadingSpinner from "./../../Loader/LoadingSpinner";
import LessonsPanel from "./LessonsPanel";
import { StudentDashboardLayout } from "./StudentDashboard";

const EnrolledCourseDetailsPage = () => {
  const { teacherCourseId } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["ui", "courses"]);
  const isArabic = i18n.language === "ar";
  const { courseDetails, loading, error } = useSelector(
    (state) => state.enrolledCourseDetails || {}
  );

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (teacherCourseId) {
      dispatch(fetchEnrolledCourseDetails(teacherCourseId));
    }
  }, [dispatch, teacherCourseId]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography color="error">{t("errorLoadingCourse")}</Typography>
      </Box>
    );
  }

  if (!courseDetails) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography>{t("noCourseFound")}</Typography>
      </Box>
    );
  }

  const {
    courseTitle,
    teacherFirstName,
    teacherLastName,
    teacherEmail,
    teacherNationality,
    startDate,
    duration,
    startTime,
    endTime,
    daysOfWeek,
    lessons,
  } = courseDetails;

  const formattedStartDate = startDate
    ? new Date(startDate).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  // Format time as "hh:mm AM/PM" and remove seconds if present
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const date = new Date();
    date.setHours(Number(h), Number(m), 0);
    return date.toLocaleTimeString(isArabic ? "ar-EG" : "en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formattedTime =
    startTime && endTime
      ? `${formatTime(startTime)} - ${formatTime(endTime)}`
      : t("courseDetails.infoBox.timeValue");

  const metaTitle = courseTitle
    ? `Enrolled Course: ${courseTitle} | ALGermanist`
    : "Enrolled Course | ALGermanist";
  const metaDescription = courseTitle
    ? `View details, schedule, and lesson plan for your enrolled course ${courseTitle}.`
    : "View details, schedule, and lesson plan for your enrolled course.";
  const metaKeywords = `enrolled course, lessons, study plan, ALGermanist${
    courseTitle ? ", " + courseTitle : ""
  }`;

  return (
    <>
      <Meta
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
      />
      <StudentDashboardLayout>
        <Box
          sx={{
            py: "32px",
            my: "64px",
            px: { xs: 2, sm: 4 },
            bgcolor: "#F6F8F8",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              my: 4,
              background: "#FFFFFF",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              borderRadius: "12px",
              mb: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              minHeight: { xs: "auto", md: 250 },
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 2, md: 3 },
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {/* Right section: Title, description, button */}
            <Box sx={{ flex: 2, px: { xs: 2, sm: 3, md: 4 } }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                mb={2}
                sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" } }}
              >
                {courseTitle
                  ? t("courseDetails.title", { courseTitle })
                  : t("courseDetails.defaultTitle")}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  textAlign: isArabic ? "right" : "left",
                  lineHeight: 2,
                }}
                mb={4}
              >
                {t("courseDetails.description")}
              </Typography>
              <Typography
                color="#2701FD"
                sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }}
                fontWeight={"bold"}
                mb={4}
              >
                {t("courseDetails.infoBox.joined")}
              </Typography>
            </Box>
            {/* Left section: Info Box */}
            <CourseInfoBox
              isArabic={isArabic}
              daysOfWeek={daysOfWeek}
              formattedStartDate={formattedStartDate}
              formattedTime={formattedTime}
              duration={duration}
            />
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                background: "#fff",
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  "&.Mui-selected": { color: "#000", fontWeight: 700 },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#FFD600",
                  height: 3,
                },
              }}
            >
              <Tab label={t("courseDetails.tabs.studyPlan")} />
              <Tab label={t("courseDetails.tabs.aboutLecturer")} />
            </Tabs>
          </Box>

          {/* Lessons */}
          {activeTab === 0 && <LessonsPanel lessons={lessons} />}

          {/* Lecturer */}
          {activeTab === 1 && (
            <Box
              sx={{
                background: "#fff",
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, sm: 3 },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 48, sm: 80 },
                  height: { xs: 48, sm: 80 },
                }}
                src="https://via.placeholder.com/150"
              />
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  {teacherFirstName} {teacherLastName}
                </Typography>
                <Typography sx={{ fontSize: { xs: 13, sm: 16 } }}>
                  {teacherEmail}
                </Typography>
                <Typography mt={2} sx={{ fontSize: { xs: 13, sm: 15 } }}>
                  {t("courseDetails.infoBox.nationality")}: {teacherNationality}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </StudentDashboardLayout>
    </>
  );
};

export default EnrolledCourseDetailsPage;
